/**
 * Works out whether a job's source clip can be embedded, and how.
 *
 * The downloaded video never leaves the machine, so a preview can only ever be
 * the original post embedded from its platform. Prefer the URL yt-dlp resolved
 * (`download.extra.webpage_url`) over the pasted `link` — shortlinks such as
 * vm.tiktok.com carry no video id, so only the resolved URL is embeddable.
 *
 * Note: `raw.source.platform` is the ingress channel ("telegram"), NOT the media
 * platform. Never derive the platform from it.
 */

const YOUTUBE_PATTERNS = [
  /youtube\.com\/watch\?(?:.*&)?v=([\w-]{6,})/i,
  /youtu\.be\/([\w-]{6,})/i,
  /youtube\.com\/shorts\/([\w-]{6,})/i,
  /youtube\.com\/embed\/([\w-]{6,})/i,
];

const TIKTOK_VIDEO_ID = /tiktok\.com\/(?:@[^/]+\/)?video\/(\d+)/i;
const INSTAGRAM_POST = /instagram\.com\/(reel|reels|p|tv)\/([\w-]+)/i;

const resolvedUrl = (job) => {
  const raw = job?.raw ?? {};
  return raw?.download?.extra?.webpage_url || job?.link || raw?.link || null;
};

export const platformOf = (job) => {
  const raw = job?.raw ?? {};
  const explicit = raw?.download?.platform || job?.platform;
  if (explicit) return explicit;

  const url = resolvedUrl(job) ?? "";
  if (/youtube\.com|youtu\.be/i.test(url)) return "youtube";
  if (/tiktok\.com/i.test(url)) return "tiktok";
  if (/instagram\.com/i.test(url)) return "instagram";
  return null;
};

/**
 * @returns {null | {src: string, platform: string, vertical: boolean, note?: string}}
 *   null when nothing embeddable could be derived.
 */
export const embedFor = (job) => {
  const url = resolvedUrl(job);
  if (!url) return null;

  for (const pattern of YOUTUBE_PATTERNS) {
    const match = url.match(pattern);
    if (match) {
      return {
        src: `https://www.youtube-nocookie.com/embed/${match[1]}`,
        platform: "youtube",
        // Shorts are vertical; the plain watch URL is not.
        vertical: /\/shorts\//i.test(url),
      };
    }
  }

  const tiktok = url.match(TIKTOK_VIDEO_ID);
  if (tiktok) {
    return {
      src: `https://www.tiktok.com/embed/v2/${tiktok[1]}`,
      platform: "tiktok",
      vertical: true,
    };
  }

  const instagram = url.match(INSTAGRAM_POST);
  if (instagram) {
    const kind = instagram[1].toLowerCase() === "reels" ? "reel" : instagram[1];
    return {
      src: `https://www.instagram.com/${kind}/${instagram[2]}/embed`,
      platform: "instagram",
      vertical: true,
      note: "Instagram often only shows a poster frame — use “open on instagram” to watch it.",
    };
  }

  return null;
};

/** Why there is no player, in words worth showing the user. */
export const embedUnavailableReason = (job) => {
  if (job?.link_type === "article") return null; // no player expected
  if (!job?.link && !job?.raw?.link) return "No source link on this job.";

  const raw = job?.raw ?? {};
  if (!raw?.download?.ok) {
    return "The clip has not been downloaded yet, so the source link cannot be resolved into a player.";
  }

  // The headless-browser fallback echoes the shortlink back as webpage_url
  // instead of resolving it, and a shortlink carries no video id to embed with.
  const url = resolvedUrl(job) ?? "";
  if (/vm\.tiktok\.com|vt\.tiktok\.com/i.test(url)) {
    return "This TikTok shortlink was never resolved to a video id during download, so it cannot be embedded.";
  }

  return "This platform cannot be embedded — open the source link instead.";
};
